const BASE = process.env.NEXT_PUBLIC_API_URL ?? "/api";

const TOKEN_KEY = "parroty.token";
const TOKENS_KEY = "parroty.tokens";

export type Player = {
  id: number;
  nickname: string;
  avatar: string | null;
  is_host: boolean;
  connection_state: string;
};

export type JoinResult = {
  code: string;
  status: string;
  player: Player & { session_token: string };
};

export type RoomState = {
  id: number;
  code: string;
  status: string;
  settings: Record<string, unknown>;
  pack_id: number | null;
  round_id: number | null;
  last_round_id: number | null;
  host_player_id: number | null;
  you: Player;
  players: Player[];
};

export type Slot = {
  id: number;
  status: string;
  input_source: string;
  input_take_id: number | null;
  input_audio_url: string | null;
  cue: {
    id: number;
    ordinal: number;
    character: string;
    caption: string;
    start_ms: number;
    duration_ms: number | null;
    image_url: string | null;
  };
};

/**
 * Media URLs need the player token, and <img>/<audio> cannot send headers —
 * so media is fetched, authorised, and handed over as a blob URL.
 */
const mediaCache = new Map<string, Promise<string>>();

/**
 * Hand every cached blob back to the browser.
 *
 * A blob URL keeps its bytes alive until it is revoked — so a room that
 * plays three packs in an evening would otherwise hold all three, plus every
 * take of every round, until the tab is closed. On a phone that ends as a
 * crashed tab, not merely a slow one.
 */
export function releaseMedia(): void {
  for (const pending of mediaCache.values()) {
    pending.then((url) => URL.revokeObjectURL(url)).catch(() => undefined);
  }

  mediaCache.clear();
}

export function mediaUrl(path: string): Promise<string> {
  const cached = mediaCache.get(path);
  if (cached) return cached;

  const loading = (async () => {
    const response = await fetch(`${BASE.replace(/\/api$/, "")}${path}`, {
      headers: { "X-Player-Token": readToken() ?? "" },
    });

    if (!response.ok) throw new ApiError("Материал не загрузился.", response.status);

    return URL.createObjectURL(await response.blob());
  })();

  // A failed load must not poison the cache forever.
  loading.catch(() => mediaCache.delete(path));
  mediaCache.set(path, loading);

  return loading;
}

export type RoundState = {
  id: number;
  mode: string;
  phase: string;
  generation: number;
  generation_count: number;
  phase_ends_at: string | null;
  submitted: number;
  total: number;
  waiting_on: number[];
  your_slots: Slot[];
};

export type PackSummary = {
  id: number;
  title: string;
  status: string;
  failure_reason: string | null;
  warnings: Array<{ file: string; reason: string }>;
  cue_count: number;
  selected: boolean;
  played: boolean;
};

export type RevealLine = {
  start_ms: number;
  duration_ms: number | null;
  character: string;
  caption: string;
  image_url: string | null;
  take_audio_url: string;
  player: { id: number; nickname: string; avatar: string | null };
  score: { rhythm: number; melody: number; timing: number; verdict: string | null } | null;
};

export type Reveal = {
  video_url: string | null;
  backing_url: string | null;
  video_duration_ms: number | null;
  timeline: RevealLine[];
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly errors: Record<string, string[]> = {},
  ) {
    super(message);
  }

  /** The first message for a field, which is what a form actually shows. */
  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0];
  }
}

/**
 * A network failure is not a message a player should read raw.
 *
 * `fetch` rejects with «Failed to fetch» — an English sentence about the
 * browser's plumbing that we then rendered inside the studio. What people
 * need to know is that the studio is unreachable and that it will come
 * back on its own.
 */
class OfflineError extends Error {
  constructor() {
    super("Нет связи со студией. Проверьте интернет — мы переподключимся сами.");
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = readToken();

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { "X-Player-Token": token } : {}),
      ...init.headers,
    },
  }).catch(() => {
    throw new OfflineError();
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      payload.message ?? "Что-то пошло не так. Попробуйте ещё раз.",
      response.status,
      payload.errors ?? {},
    );
  }

  return payload as T;
}

export function readToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

/**
 * The token is the only credential a player has, and it is what lets them back
 * into a room after a dropped connection — so it outlives the tab.
 *
 * Kept per room as well as "current": one player in two rooms is normal (make
 * a room, follow a friend's link, come back), and a single slot meant
 * returning to the first room silently presented the second room's token.
 */
export function storeToken(token: string, code?: string): void {
  window.localStorage.setItem(TOKEN_KEY, token);

  if (code) {
    const map = JSON.parse(window.localStorage.getItem(TOKENS_KEY) ?? "{}");
    map[code.toUpperCase()] = token;
    window.localStorage.setItem(TOKENS_KEY, JSON.stringify(map));
  }
}

/** Entering a room makes its remembered token the current one, if we have it. */
export function activateRoom(code: string): boolean {
  const map = JSON.parse(window.localStorage.getItem(TOKENS_KEY) ?? "{}");
  const token = map[code.toUpperCase()];

  if (typeof token === "string") {
    window.localStorage.setItem(TOKEN_KEY, token);

    return true;
  }

  return false;
}

export const api = {
  createRoom: (nickname: string, avatar?: string) =>
    request<JoinResult>("/rooms", {
      method: "POST",
      body: JSON.stringify({ nickname, avatar }),
    }),

  joinRoom: (code: string, nickname: string, avatar?: string) =>
    request<JoinResult>(`/rooms/${code}/join`, {
      method: "POST",
      body: JSON.stringify({ nickname, avatar }),
    }),

  showRoom: (code: string) => request<RoomState>(`/rooms/${code}`),

  showRound: (id: number) => request<RoundState>(`/rounds/${id}`),

  startExport: (roundId: number) =>
    request<{ status: string }>(`/rounds/${roundId}/export`, { method: "POST" }),
  exportStatus: (roundId: number) =>
    request<{ status: string | null; extension: string | null }>(`/rounds/${roundId}/export`),
  start: (code: string, mode?: string) =>
    request<RoundState>(`/rooms/${code}/start`, {
      method: "POST",
      body: JSON.stringify(mode ? { mode } : {}),
    }),

  reveal: (roundId: number) => request<Reveal>(`/rounds/${roundId}/reveal`),

  changeAvatar: (avatar: string) =>
    request<{ avatar: string }>("/players/me", {
      method: "PATCH",
      body: JSON.stringify({ avatar }),
    }),

  leave: (code: string) => request<{ left: boolean }>(`/rooms/${code}/leave`, { method: "POST" }),

  packs: (code: string) => request<{ packs: PackSummary[] }>(`/rooms/${code}/packs`),

  deletePack: (packId: number) =>
    request<{ deleted: boolean; pack_id: number | null }>(`/packs/${packId}`, { method: "DELETE" }),
  choosePack: (code: string, packId: number) =>
    request<{ pack_id: number }>(`/rooms/${code}/packs/${packId}`, { method: "POST" }),

  // The server files the pack under the uploader's own room, taken from the
  // token — the client does not get to say where it lands.
  uploadPack: (archive: File, origin: string) => {
    const form = new FormData();
    form.append("archive", archive);
    // Asked, not assumed: the attestation is only worth storing if the
    // uploader actually answered it (spec §10).
    form.append("origin", origin);
    form.append("rights_attested", "1");

    return request<{ id: number; status: string }>("/packs", { method: "POST", body: form });
  },

  submitTake: (slotId: number, audio: Blob, meta: Record<string, number | string> = {}) => {
    const form = new FormData();
    // Safari records AAC-in-mp4, Chrome opus-in-webm; playback decodes
    // bytes, not names, but the name should still tell the truth.
    const kind = audio.type.includes("mp4") || audio.type.includes("aac")
      ? "m4a"
      : audio.type.includes("ogg")
        ? "ogg"
        : "webm";
    form.append("audio", audio, `take.${kind}`);

    for (const [key, value] of Object.entries(meta)) {
      form.append(key, String(value));
    }

    return request<{ take_id: number; outstanding: number }>(`/slots/${slotId}/take`, {
      method: "POST",
      body: form,
    });
  },
};
